import { DateTime } from 'luxon';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  AccountLedgerEntry,
  AccountLedgerRequest,
  AccountTotalsRow,
  GeneralLedgerAccount,
  PeriodicLedgerTotal,
} from '../../clients/quipli-service/accounting/accounting';
import React, { useCallback, useEffect, useState } from 'react';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { DateTimeRange } from '../../clients/quipli-service/report/report';
import { useAccountingApiV1 } from '../../clients/quipli-service/accounting/accounting-api-v1';
import FileSaver from 'file-saver';
import { toast } from '../../util/toasts';
import { DatePicker } from '../../components/date-picker/date-picker';
import { Button } from '../../components/form/button';
import Icon from '../../components/icons/icon';
import { Card } from '../../components/card/card';
import { Spinner } from '../../components/spinner/spinner';
import { displayMoneyFormat } from '../../util/util';
import { Pagination } from '../../clients/quipli-service/pagination/pagination';
import { Paginator } from '../../components/paginator/paginator-v2';
import { Location } from '../../clients/quipli-service/location/location';
import { SelectForm } from '../../components/select-form/select-form';
import { BarChart } from '../../components/charts/bar-chart';
import { ButtonGroup } from '../../components/button-group/button-group';
import { SpinnerModal } from '../../components/modal/spinner-modal';

function extractAccountLedgerReportSearchParams(params: URLSearchParams): AccountLedgerRequest {
  const from = params.get('from') ? DateTime.fromISO(params.get('from')!) : undefined;
  const to = params.get('to') ? DateTime.fromISO(params.get('to')!) : undefined;
  const locationId = params.get('locationId') ? parseInt(params.get('locationId')!) : undefined;
  const pageNumber = params.get('pageNumber') ? parseInt(params.get('pageNumber')!) : undefined;
  const pageSize = params.get('pageSize') ? parseInt(params.get('pageSize')!) : undefined;
  const basis = (params.get('basis') as 'accrual' | 'cash') || 'accrual';
  return { from, to, locationId, pageNumber, pageSize, basis };
}

function injectAccountLedgerSearchParams(params: URLSearchParams, req: AccountLedgerRequest): URLSearchParams {
  req.from ? params.set('from', req.from.toISO()) : params.delete('from');
  req.to ? params.set('to', req.to.toISO()) : params.delete('to');
  req.locationId ? params.set('locationId', req.locationId.toString()) : params.delete('locationId');
  req.pageNumber ? params.set('pageNumber', req.pageNumber.toString()) : params.delete('pageNumber');
  req.pageSize ? params.set('pageSize', req.pageSize.toString()) : params.delete('pageSize');

  return params;
}

export const AccountLedger = () => {
  const { accountId } = useParams();
  const { accountLedger, exportAccountLedger, getAccount, getAvailableAccountLedgerRange, accountTotals } =
    useAccountingApiV1();
  const { getMultipleConfigurations, listLocations } = useAPI();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [timezone, setTimezone] = useState<string>();
  const [currency, setCurrency] = useState<string>();
  const [availableAccountLedgerRange, setAvailableAccountLedgerRange] = useState<DateTimeRange>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ledgerRequest, setLedgerRequest] = useState<AccountLedgerRequest>(
    extractAccountLedgerReportSearchParams(searchParams),
  );
  const [account, setAccount] = useState<GeneralLedgerAccount>();
  const [ledger, setLedger] = useState<Pagination<AccountLedgerEntry>>();
  const [totals, setTotals] = useState<AccountTotalsRow>();
  const [locations, setLocations] = useState<Location[]>();
  const [isExporting, setIsExporting] = useState<boolean>(false);

  useEffect(() => {
    getAvailableAccountLedgerRange(accountId!, ledgerRequest).then(setAvailableAccountLedgerRange);
  }, [accountId, ledgerRequest]);

  useEffect(() => {
    loadTimezone();
  }, []);

  const loadTimezone = async () => {
    const [confs, locations] = await Promise.all([
      getMultipleConfigurations(['CONF_TIMEZONE', 'CONF_CURRENCY']),
      listLocations(),
    ]);
    const timezone = confs.find((c) => c.variable === 'CONF_TIMEZONE')?.value;
    const currency = confs.find((c) => c.variable === 'CONF_CURRENCY')?.value;
    setCurrency(currency || 'USD');
    setTimezone(timezone);
    setLocations(locations);
  };

  async function loadReport() {
    if (timezone && Object.keys(ledgerRequest).length > 0) {
      const previousSearchString = searchParams.toString();
      const newParams = injectAccountLedgerSearchParams(searchParams, ledgerRequest);
      if (previousSearchString !== newParams.toString()) {
        setSearchParams(newParams);
      }
      setIsLoading(true);
      try {
        const [ledger, account, totals] = await Promise.all([
          accountLedger(accountId!, ledgerRequest),
          getAccount(accountId!),
          accountTotals({
            from: ledgerRequest.from,
            to: ledgerRequest.to,
            accountIds: [accountId!],
            basis: ledgerRequest.basis,
          }),
        ]);
        setLedger(ledger);
        setAccount(account);
        setTotals(totals[0]);
      } catch (e: any) {
        if (e.status === 400) {
          toast(e.message, 'error');
        } else {
          toast('Error loading account ledger', 'error');
        }
      }
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setLedgerRequest(extractAccountLedgerReportSearchParams(searchParams));
  }, [searchParams]);

  useEffect(() => {
    loadReport();
  }, [ledgerRequest, timezone]);

  const exportReport = async () => {
    if (ledgerRequest) {
      setIsExporting(true);
      try {
        const file = await exportAccountLedger(accountId!, {
          from: ledgerRequest.from,
          to: ledgerRequest.to,
          locationId: ledgerRequest.locationId,
          basis: ledgerRequest.basis,
        });

        const exportFileName = `${account?.name}_Ledger_${(ledgerRequest.from ?? DateTime.local())
          .toFormat('D')
          .replaceAll('/', '-')}_${(ledgerRequest.to ?? DateTime.local()).toFormat('D').replaceAll('/', '-')}`;
        const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const fileExtension = '.xlsx';

        const data = new Blob([file], { type: fileType });
        FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
      } catch (e: any) {
        toast('Error exporting account ledger', 'error');
        console.error(e);
      } finally {
        setIsExporting(false);
      }
    }
  };

  const reportingMonths: DateTimeRange[] = [];
  if (availableAccountLedgerRange) {
    let currentStart = availableAccountLedgerRange.from.startOf('month');
    while (currentStart.diffNow().milliseconds < 0) {
      reportingMonths.push({ from: currentStart, to: currentStart.endOf('month') });
      currentStart = currentStart.plus({ months: 1 }).setZone(timezone, { keepLocalTime: true });
    }
  }
  reportingMonths.reverse();
  let selectedMonth = -1;
  if (ledgerRequest.from && ledgerRequest.to) {
    selectedMonth = reportingMonths.findIndex(
      (m) => m.from.diff(ledgerRequest.from!).milliseconds === 0 && m.to.diff(ledgerRequest.to!).milliseconds === 0,
    );
  }

  const buttonItems: { label: string; value: 'cash' | 'accrual' }[] = [
    { label: 'Cash', value: 'cash' },
    { label: 'Accrual', value: 'accrual' },
  ];

  return (
    <div className="account-totals container-v2 p-2 flex flex-column gap-4">
      {account && ledger ? (
        <>
          <div className="flex justify-between items-center">
            <h1 className="m-0">{account.name}</h1>
          </div>
          {ledgerRequest && (
            <div className="tax-report flex flex-column gap-4">
              <div className="flex flex-wrap justify-between items-end gap-4">
                <div className="flex flex-wrap items-start gap-4">
                  <div className="flex flex-col">
                    <label className="font-medium">Month</label>
                    <select
                      className="h-fit text-[14px]"
                      value={selectedMonth}
                      required={false}
                      onChange={(e) => {
                        const range = reportingMonths[parseInt(e.target.value)];
                        setLedgerRequest((prevState) => ({
                          ...prevState,
                          from: range.from,
                          to: range.to,
                        }));
                      }}
                    >
                      {selectedMonth < 0 && <option value={-1}></option>}
                      {reportingMonths.map((m, i) => (
                        <option key={i} value={i}>
                          {m.from.toFormat('LLLL yyyy')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="">
                    <label className="font-medium">From</label>
                    <DatePicker
                      value={ledgerRequest.from}
                      clearable={true}
                      onChange={(from) =>
                        setLedgerRequest((prevState) => ({
                          ...prevState,
                          from: from?.setZone(timezone).startOf('day'),
                        }))
                      }
                    />
                  </div>
                  <div className="">
                    <label className="font-medium">To</label>
                    <DatePicker
                      value={ledgerRequest.to}
                      clearable={true}
                      onChange={(to) =>
                        setLedgerRequest((prevState) => ({
                          ...prevState,
                          to: to?.setZone(timezone).endOf('day'),
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="font-medium">Location</label>
                    <SelectForm
                      styles={{
                        menu: (provided) => ({ ...provided, zIndex: 9999 }),
                      }}
                      value={locations?.find((l) => l.id === ledgerRequest.locationId)}
                      options={locations?.filter(
                        (l) =>
                          l.id === ledgerRequest.locationId ||
                          l.isActive ||
                          ledger.items.some((line) => line.Location === l.name),
                      )}
                      getOptionLabel={(l) => l.name}
                      getOptionValue={(l) => l.id.toString()}
                      onChange={(l) =>
                        setLedgerRequest((r) => {
                          return { ...r, locationId: l?.id };
                        })
                      }
                      isClearable={true}
                      menuPlacement="bottom"
                      placeholder="Select"
                    />
                  </div>
                  {account.accountType === 'Tax' && (
                    <div className="justify-between gap-2">
                      <label className="font-medium">Tax Reporting Basis</label>
                      <ButtonGroup<'cash' | 'accrual'>
                        items={buttonItems}
                        onClick={(value) =>
                          setLedgerRequest((prev) => ({ ...prev, basis: value as 'cash' | 'accrual' }))
                        }
                        selected={ledgerRequest.basis}
                      />
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button className="button-yellow font-semibold" onClick={exportReport} data-cy="export">
                    Export
                    <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          {totals && locations && ledgerRequest && currency && (
            <SummaryStats
              totals={totals}
              location={locations.find((l) => l.id === ledgerRequest.locationId)}
              currency={currency}
            />
          )}

          {availableAccountLedgerRange && ledgerRequest && currency && (
            <TimeSeriesBarChart
              accountId={accountId!}
              from={ledgerRequest.from || availableAccountLedgerRange.from}
              to={ledgerRequest.to || availableAccountLedgerRange.to}
              locationId={ledgerRequest.locationId}
              currency={currency}
              basis={ledgerRequest.basis ?? 'accrual'}
            />
          )}

          <Card>
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left">Invoice Number</th>
                  <th className="text-left">Name</th>
                  <th className="text-left">Description</th>
                  <th className="text-left">Location</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={100}>
                      <Spinner />
                    </td>
                  </tr>
                ) : (
                  <>
                    {currency && ledger && (
                      <>
                        {ledger.items.length > 0 ? (
                          ledger.items.map((entry, i) => <TableRow key={i} entry={entry} currency={currency} />)
                        ) : (
                          <tr>
                            <td colSpan={7}>No record found</td>
                          </tr>
                        )}
                      </>
                    )}
                  </>
                )}
              </tbody>
            </table>
            <Paginator
              pagination={ledger?.pagination}
              request={ledgerRequest}
              setRequest={(pag) =>
                setLedgerRequest((req) => {
                  return { ...req, ...pag };
                })
              }
            />
          </Card>
          <SpinnerModal isOpen={isExporting} contentDescription={'Exporting account ledger'} />
        </>
      ) : (
        <Spinner />
      )}
    </div>
  );
};

const TableRow = ({ entry, currency }: { entry: AccountLedgerEntry; currency: string }) => {
  return (
    <tr>
      <td className="p-2">{entry['Date']}</td>
      <td>{entry['Invoice Number']}</td>
      <td>{entry['Name']}</td>
      <td>{entry['Description']}</td>
      <td>{entry['Location']}</td>
      <td className="text-right">{displayMoneyFormat(entry['Amount'], currency)}</td>
    </tr>
  );
};

export const SummaryStats = ({
  totals,
  location,
  currency,
}: {
  totals: AccountTotalsRow;
  location?: Location;
  currency: string;
}) => {
  function summaryCard(name: string, value: number | string, key?: any) {
    return (
      <Card key={key} className="w-[calc(20%-0.25rem)] p-4 flex flex-column gap-2 text-[0.9rem]">
        <div className="text-[var(--grey-500)]">{name}</div>
        <div className="font-medium text-[var(--quipli-black-800)]">
          {typeof value === 'string' ? value : displayMoneyFormat(value, currency)}
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {!location && summaryCard('Total', totals.Total)}
      {totals['Location Totals']
        .filter((lt) => !location || lt.Location === location.name)
        .map((lt, i) => summaryCard(lt.Location, lt.Total, i))}
    </div>
  );
};

export const TimeSeriesBarChart = ({
  from,
  to,
  locationId,
  accountId,
  currency,
  basis,
}: {
  from: DateTime;
  to: DateTime;
  locationId?: number;
  accountId: string;
  currency: string;
  basis: 'cash' | 'accrual';
}) => {
  const { getPeriodicLedgerTotals } = useAccountingApiV1();
  const [periodicLedgerTotals, setPeriodicLedgerTotals] = useState<PeriodicLedgerTotal[]>();

  const weeksDiff = to.diff(from).as('weeks');
  let period: 'day' | 'week' | 'month';
  if (weeksDiff < 5) {
    period = 'day';
  } else if (weeksDiff <= 12) {
    period = 'week';
  } else {
    period = 'month';
  }

  useEffect(() => {
    getPeriodicLedgerTotals(accountId, { from, to, locationId, period, basis })
      .then(setPeriodicLedgerTotals)
      .catch((e) => {
        if (e.status === 400) {
          toast(e.message, 'error');
        } else {
          toast('Error loading ledger totals', 'error');
        }
      });
  }, [from, to, locationId, accountId, basis]);

  const labelFormatter = useCallback(
    (dt: DateTime) => {
      switch (period) {
        case 'day':
          return dt.toLocaleString({ day: 'numeric', month: 'numeric' });
        case 'week':
          return `${dt.toLocaleString({ day: 'numeric', month: 'numeric' })} - ${dt
            .plus({ days: 6 })
            .toLocaleString({ day: 'numeric', month: 'numeric' })}`;
        case 'month':
          return dt.toFormat('MMM y');
      }
    },
    [period],
  );

  return periodicLedgerTotals ? (
    <Card>
      <BarChart
        className="h-[15rem] p-4"
        data={periodicLedgerTotals.slice(-31).map((m) => {
          return { label: labelFormatter(m.periodStart), value: m.total };
        })}
        valueFormatter={(v) => displayMoneyFormat(v, currency)}
      />
    </Card>
  ) : (
    <Spinner />
  );
};
